import type { Messages } from './en';

/** Textos de la fase D (vida): paga, hucha, necesidades y caprichos, anuncios, fichas, exposición, escucha y hamburguesería. */
export const esLife: Pick<Messages, 'life'> = {
  life: {
    mapNames: {} as Record<string, string>,
    zones: {
      fastFood: 'Burger Corner',
    },
    objects: {
      life_piggy: 'Hucha',
      life_fastfood: 'Burger Corner',
      life_adscreen: 'Pantalla de anuncios',
    },
    npc: {} as Record<string, string>,
    items: {
      umbrella: { name: 'Paraguas', desc: 'Te mantiene seco cuando llueve. ¡En los días de lluvia es una necesidad!' },
    } as Record<string, { name: string; desc: string }>,

    need: 'Necesidad',
    want: 'Capricho',
    mom: 'Mamá',
    coins: (n: number) => `${n}🪙`,

    allowance: {
      title: '💰 Paga semanal',
      text: '¡Buenos días! Aquí tienes tu paga de la semana: **100 monedas**.\n¿Cómo la vas a repartir? Una parte para gastar, otra para la hucha y otra para regalos.',
      split: 'Vamos a planearlo',
      hint: 'Toca − y + para mover monedas. Tiene que sumar 100.',
      spend: '🛍️ Para gastar',
      piggy: '🐷 Hucha',
      gift: '🎁 Fondo de regalos',
      presets: { balanced: '⚖️ Equilibrado', saver: '🐷 Ahorrador', spender: '🛍️ Gastador' },
      left: (n: number) => (n === 0 ? '✅ ¡Las 100 monedas tienen trabajo!' : n > 0 ? `A ${n} monedas les falta trabajo` : `Sobran ${-n} monedas`),
      confirm: 'Este es mi plan',
      momSaver: '¡Hala, cuánto ahorras! Los domingos añado un 10 % a lo que tengas en la hucha. ¡Ya verás cómo crece!',
      momBalanced: 'Un plan muy equilibrado. Gastar un poco, ahorrar un poco y compartir un poco.',
      momSpender: '¡Eso es mucho para gastar! Tú decides, pero recuerda: cuando se acaba, se acaba hasta el sábado que viene.',
    },

    interest: {
      title: '🐷 El extra del domingo',
      text: (add: number, total: number) =>
        `Mamá echa un vistazo a tu hucha y añade **${add} monedas**: el 10 % de lo que has ahorrado.\n¡Ahora hay **${total} monedas** dentro!\n\nAsí es como un banco paga «intereses»: el dinero que dejas ahorrado crece solo.`,
      empty: 'Mamá mira tu hucha: está vacía. «Cada domingo añado un 10 % a lo que hayas ahorrado. ¡Hasta 10 monedas crecerían!»',
    },

    piggy: {
      text: (piggy: number, coins: number) => `Un cerdito rosa con una ranura para monedas. Dentro: **${piggy}🪙**. En el bolsillo: **${coins}🪙**.`,
      in: (n: number) => `Meter ${n}🪙`,
      out: (n: number) => `Sacar ${n}🪙`,
      all: 'Meterlo todo',
      done: (piggy: number) => `¡Clinc! Ahora la hucha tiene ${piggy}🪙.`,
      outWarn: 'Sacar dinero no está mal; solo pregúntate: ¿es para una necesidad o para un capricho?',
      wallet: '👛 Abrir mi monedero',
    },

    nw: {
      title: 'Pregunta rápida',
      ask: (item: string) => `Antes de pagar: ¿**${item}** es una necesidad o un capricho?`,
      right: (item: string, kind: string) => `¡Correcto! ${item} es: **${kind}**.`,
      wrong: (item: string, kind: string) => `Mmm, ${item} suele ser: **${kind}**.`,
      needWhy: 'Las necesidades te mantienen sano, seguro y preparado para el cole.',
      wantWhy: 'Los caprichos son extras agradables. Está bien comprarlos, pero planéalos antes.',
      buy: 'Comprarlo',
    },

    ad: {
      sources: { tv: '📺 Anuncio de la tele', mall: '🏬 Pantalla del centro comercial', phone: '📱 Ventana emergente del móvil' },
      flash: '¡OFERTA LIMITADA!',
      only: '¡SOLO HOY!',
      was: (n: number) => `Antes ${n}🪙`,
      now: (n: number) => `AHORA ${n}🪙`,
      timer: (s: number) => `La oferta acaba en ${s}…`,
      buy: (n: number) => `Comprar ya (${n}🪙)`,
      wait: '⏳ Esperar hasta mañana',
      boughtLesson: (was: number, now: number) =>
        `¡Lo has comprado! Pero fíjate bien: el precio de «antes ${was}» es inventado, y ${now} es el precio normal.\nLos anuncios usan palabras con prisa como **LIMITADA** y **SOLO HOY** para que compres antes de pensar.`,
      waitLesson: '¡Muy listo! Lo has añadido a tu lista de deseos. Un buen truco: espera un día antes de comprar un capricho. Si mañana aún lo quieres, todavía podrás comprarlo.',
      poor: 'No tienes suficientes monedas… ¡y aun así el anuncio quiere que sientas que lo necesitas!',
    },

    wish: {
      title: '📝 Tu lista de deseos',
      cheaper: (item: string, was: number, now: number) => `¿Te acuerdas de ${item} del anuncio? Hoy cuesta **${now}🪙** en vez de ${was}🪙. ¡La oferta «limitada» no era tan limitada!`,
      forgot: (item: string) => `Te despiertas y piensas en ${item} del anuncio… en realidad no lo necesitas. ¡Te has quedado las monedas!`,
      lesson: 'Esperar un día te ayuda a distinguir una necesidad de verdad de un capricho pasajero.',
      buy: (n: number) => `Comprarlo ahora por ${n}🪙`,
      skip: 'No, gracias, me quedo las monedas',
      kept: 'Monedas ahorradas. ¡Tu yo del futuro te da las gracias!',
    },

    wallet: {
      title: '👛 Mi monedero',
      menu: '👛 Monedero',
      pocket: 'Bolsillo',
      piggy: 'Hucha',
      gift: 'Fondo de regalos',
      budget: 'Presupuesto de esta semana',
      spent: (s: number, b: number) => (b ? `${s} de ${b}🪙 gastadas` : `${s}🪙 gastadas (aún sin plan: la paga llega el sábado)`),
      over: '¡Te has pasado del presupuesto! Intenta esperar a la semana que viene para los caprichos.',
      donut: 'Necesidades y caprichos esta semana',
      none: 'Esta semana aún no has gastado nada.',
      allTime: (n: number, w: number) => `En total: ${n}🪙 en necesidades · ${w}🪙 en caprichos`,
      wishes: 'Lista de deseos',
      noWishes: 'Vacía: no hay compras impulsivas esperando.',
      quiz: (n: number) => `Aciertos de necesidad o capricho: ${n}`,
      tip: '💡 Consejo',
      tips: [
        'Págate a ti primero: mete monedas en la hucha antes de ir de compras.',
        'Un capricho no es malo: planéalo y te sentará aún mejor cuando lo compres.',
        '«¡Antes 40, ahora 25!»: el precio de «antes» muchas veces es inventado.',
        'Los menús solo te ahorran dinero si quieres todo lo que llevan.',
        'Ahorrar 10 monedas a la semana son 520 monedas al año.',
        'Antes de comprar, pregúntate: ¿lo necesito? ¿Tengo sitio para ello? ¿Lo seguiré queriendo la semana que viene?',
        'Compara el precio de UNO: 3 por 45 son 15 cada uno; 2 por 28 son 14 cada uno.',
      ],
    },

    ws: {
      kinds: { homework: '📒 Deberes', quiz: '📝 Control', exam: '🧪 Examen' },
      gradeLabel: (g: number) => `${g}.º curso`,
      progress: (i: number, n: number) => `Pregunta ${i} de ${n}`,
      types: { spell: '🔤 Deletreo', cloze: '✏️ Rellena el hueco', math: '🪙 Mates con dinero', reading: '📖 Lectura', logic: '🧩 Lógica' },
      spellHint: 'Escucha y toca las letras en orden.',
      clozeHint: 'Elige la palabra que falta.',
      mathHint: 'Piénsalo bien: ¡usa papel de sucio si quieres!',
      readingHint: 'Lee el texto y luego responde.',
      logicHint: 'Encuentra el patrón.',
      hear: '🔊 Escuchar',
      undo: '⌫ Deshacer',
      next: 'Siguiente ▶',
      finish: 'Ver mi nota',
      right: '✅ ¡Correcto!',
      wrong: (a: string) => `❌ La respuesta es: ${a}`,
      result: (s: number, n: number) => `${s} / ${n} correctas`,
      comments: { A: '¡Trabajo excelente! 🌟', B: 'Buen trabajo: ¡un poco más de práctica!', C: 'Sigue intentándolo: los errores te ayudan a aprender.' },
      done: 'Entregar',
      math: {
        items: ['onigiri', 'zumo', 'lápiz', 'cromo', 'cuaderno', 'llavero'],
        left: (a: number, item: string, b: number) => `Tienes ${a} monedas. Compras un ${item} por ${b} monedas. ¿Cuántas monedas te quedan?`,
        change: (n: number, item: string, p: number, pay: number) => `Compras ${n} × ${item} a ${p} monedas cada uno y pagas con ${pay} monedas. ¿Cuánto te devuelven?`,
        discount: (item: string, p: number, d: number) => `Un ${item} cuesta ${p} monedas. Hoy tiene un ${d}% de descuento. ¿Cuál es el nuevo precio?`,
        weeks: (x: number, item: string, y: number) => `Ahorras ${x} monedas cada semana. ¿Cuántas semanas necesitas para comprar un ${item} de ${y} monedas?`,
        unit: (item: string, nA: number, pA: number, nB: number, pB: number) =>
          `Pack A: ${nA} × ${item} por ${pA} monedas. Pack B: ${nB} × ${item} por ${pB} monedas. ¿Cuánto cuesta UN ${item} en el pack más barato?`,
        interest: (p: number) => `Tienes ${p} monedas en la hucha. Mamá añade un 10% cada semana (sobre el nuevo total). ¿Cuántas monedas tendrás después de 2 semanas?`,
      },
      logic: (seq: string) => `¿Qué viene después?  ${seq}, ?`,
    },

    desk: {
      portfolio: '🖼️ Mi portafolio',
      worksheet: 'Tu ficha de deberes está lista: cinco preguntas para tu curso.',
    },

    exhibit: {
      title: '🖼️ Exposición de trabajos',
      titlePortfolio: '🖼️ Mi portafolio',
      today: '¡Hoy es el **día de la exposición** (成果展)! Cada cuatro semanas el cole enseña lo que han hecho los alumnos. Tus fichas, dibujos y sellos están en la pared.',
      see: 'Ver mi rincón',
      works: 'Mejores fichas',
      noWorks: 'Aún no hay fichas: haz los deberes en el escritorio.',
      drawings: 'Dibujos de PixelBook',
      noDrawings: 'Aún no hay dibujos: dibuja algo en PixelBook con tu móvil.',
      stamps: 'Sellos del pasaporte',
      noStamps: 'Aún no hay sellos: ¡ve de excursión y celebra las fiestas!',
      reward: (n: number) => `¡A los visitantes les encanta tu rincón! +${n}⭐`,
      next: (w: number) => `Próxima exposición: semana ${w}`,
    },

    listen: {
      tab: '👂 Escuchar',
      pick: '👂 Escucha y elige',
      dict: '⌨️ Dictado',
      pickHint: 'Escucha y toca lo que has oído.',
      dictHint: 'Escucha y escribe lo que oyes.',
      play: '🔊 Reproducir',
      slow: '🐢 Lento',
      check: 'Comprobar',
      placeholder: 'Escribe aquí…',
      ok: '¡Perfecto!',
      close: (a: string) => `¡Casi! Era: «${a}»`,
      bad: (a: string) => `No del todo. Era: «${a}»`,
      next: 'Siguiente ▶',
      streak: (n: number) => `🔥 Racha: ${n}`,
      shadow: '🎤 Dilo tú',
    },

    replay: { again: '🔊', slow: '🐢', mic: '🎤', listening: 'Escuchando…', good: '¡Muy bien repetido!', retry: (h: string) => `He oído «${h}». ¡Inténtalo otra vez!` },

    grade: { title: '🎒 Curso escolar', hint: 'Ajusta la dificultad de los deberes y los exámenes.', names: ['3.º', '4.º', '5.º', '6.º'] },

    ff: {
      title: '🍔 Burger Corner',
      hint: '¡Pide en inglés y compara precios!',
      menu: [
        { name: 'Hamburguesa con queso', desc: 'Calentita y te llena', en: 'a cheeseburger' },
        { name: 'Patatas fritas', desc: 'Saladas y crujientes', en: 'some fries' },
        { name: 'Refresco de cola', desc: 'Muy dulce (¡mucho azúcar!)', en: 'a cola' },
        { name: 'Menú', desc: 'Hamburguesa + patatas + cola', en: 'the combo meal' },
        { name: '🥗 Wrap vegetal + leche de soja', desc: 'La opción sana', en: 'a veggie wrap and a soy milk' },
      ],
      compare: '🧮 ¿Menú o no?',
      compareText: (sum: number, combo: number) => `Hamburguesa + patatas + cola por separado = **${sum}🪙**. El menú = **${combo}🪙**. Ahorras ${sum - combo}🪙… ¡pero solo si quieres las tres cosas!`,
      water: '¿Agua gratis? Solo tienes que pedirla: “Can I have some water, please?”',
      cashier: 'Cajero',
      greet: 'Hi! Welcome to Burger Corner. What can I get for you?', // English practice
      prompt: 'Elige la forma educada de pedir:',
      wrong: ['El cajero parpadea. “Sorry? Could you say that politely?”', 'Mmm, eso suena un poco maleducado. Prueba con “Can I have…, please?”'],
      done: 'Here you go! Enjoy your meal!', // English practice
      poor: 'No tienes suficientes monedas. ¿Quizá algo más barato?',
      healthy: '¡Una elección sana! Tu cuerpo te lo agradece. +2⭐',
      tooMuch: 'Eso llevaba mucha sal y mucho azúcar… te notas un poco pesado.',
      look: 'Solo estoy mirando, gracias',
      waterDone: 'Sure! Here’s a cup of water.', // English practice
    },

    save: {
      menu: '🔑 Código de guardado',
      title: '🔑 Código de guardado',
      exportHint: 'Copia este código para guardar tu partida o pasarla a otro dispositivo.',
      copy: '📋 Copiar código',
      copied: '¡Copiado!',
      importHint: 'Pega aquí un código de guardado para cargar esa partida.',
      placeholder: 'Pega un código…',
      load: '📥 Cargar este código',
      bad: 'Ese código no funciona. Comprueba que lo has copiado entero.',
      confirm: '¿Cargar esta partida? Se sustituirá la partida actual.',
      startButton: '🔑 Cargar un código de guardado',
    },

    journal: {
      allowance: (split: string) => `He planeado mi paga: ${split}`,
      exhibit: 'He enseñado mis trabajos en la exposición',
      waited: 'He esperado un día en vez de comprar por impulso',
      worksheet: (g: string) => `He sacado una ${g} en una ficha`,
    },
  },
};
